import { Checkbox } from '@evertrue/et-components';
import { fetchFromGraphql } from 'entities/helpers/gqlCall';
import { useInteractionFormContext } from 'apps/interactions/form/context/interaction-form-context';
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import ContactTagList from 'apps/contact/components/contact-tag-list';
import CachedContactSource from 'apps/contact/sources/cached-contact-source';
import CachedContactsResolver from 'apps/contact/components/contacts/cached-contacts-resolver';

// Helper function to fetch household members for a contact
// This is used to populate the household members in the interaction form
const getHouseholdMembersForContact = async (id) => {
  if (!id) return [];

  const gql = String.raw;
  const query = gql`
    query ($id: BigInt!) {
      contact(id: $id) {
        household_members {
          id
          contact_attributes {
            name_full
          }
        }
      }
    }
  `;

  try {
    const params = { id };
    const result = await fetchFromGraphql(query, params, 'getHouseholdMembersForContact');

    return result?.contact?.household_members || [];
  } catch (e) {
    console.error(`Failed to fetch household members. Error: ${e}`);
    return [];
  }
};

/**
 * This component is designed to be used inside an Interaction Form using Formik.
 *
 * It is really two components in one (which is why it looks so complicated):
 * 1. A ContactTagList that allows the user to select additional constituents (secondary targets) for the interaction
 * 2. A set of checkboxes that allow the user to add household members of the primary contact as secondary targets
 *
 * The ContactTagList is a text input users can type into to search for contacts.
 * The checkboxes can be checked to add/remove household members as secondary targets.
 *
 * @param {object} field - Formik field object
 * @param {object} form - Formik form object
 * @param {object} contact - Contact object
 */
const SecondaryTargetField = ({ onChange, onBlur, name, value, contactId, required = false, field, meta, helpers }) => {
  // This is a number that users set in DNA to limit the number of secondary targets on an interaction
  const { secondaryTargetLimit } = useInteractionFormContext();

  // This is an array of contact IDs that are listed as secondary targets
  const targetIds = useMemo(() => {
    return value?.filter((v) => !!v.target_id).map((v) => v.target_id) || [];
  }, [value]);

  // When this component renders, fetch the contact data for the secondary target IDs and cache that information
  useEffect(() => {
    CachedContactSource.fetchContacts(targetIds);
  }, [targetIds]);

  // Household Members are the people that are in the same household as the primary contact
  // We want to show these as checkboxes to add them as secondary targets
  const [householdMembers, setHouseholdMembers] = useState([]);

  // Populate the household members for the primary contact
  useEffect(() => {
    const getHouseholdMembers = async () => {
      if (contactId) {
        try {
          const members = await getHouseholdMembersForContact(contactId);
          setHouseholdMembers(members);
        } catch (e) {
          console.error(`Failed to fetch household members. Error: ${e}`);
        }
      }
    };

    getHouseholdMembers();
  }, [contactId]);

  // This function handles the selection of secondary targets
  // it seems complicated because it needs to handle both the ContactTagList and the checkboxes
  const handleSelect = (selectedContacts = [], isCheckbox = false, checked) => {
    let newValue = selectedContacts || [];
    if (selectedContacts?.length <= secondaryTargetLimit) {
      if (isCheckbox) {
        if (checked) {
          newValue = [...value, ...selectedContacts];
        }
        if (!checked) {
          newValue = value.filter((v) => v.id !== selectedContacts[0].id);
        }
      }
    }

    onChange({
      target: {
        name,
        value: newValue,
      },
    });
  };

  // This function filters out the primary contact and household members from the list of search results
  const contactsFilterFunction = (contact) => {
    const excludedIds = [contactId, ...householdMembers.map((mem) => mem.id)];
    return !excludedIds.includes(contact.id);
  };

  // Wrap the component in a resolver to fetch the contact data for the secondary target IDs
  return (
    <CachedContactsResolver ids={targetIds}>
      {({ contacts = [], loading }) => {
        // This is the array of contacts that are selected as secondary targets
        const selected = value.map((existing = {}) => {
          // If the contact already has a name and ID, use that
          if ((existing.name && existing.id) || existing?.contact_attributes?.name_full) return existing;
          // otherwise use the contact data from the cache
          const { target_id } = existing;
          return contacts[target_id];
        });

        return (
          <>
            {Number(secondaryTargetLimit) > 0 ? (
              <>
                <ContactTagList
                  contactsFilterFunction={contactsFilterFunction}
                  selected={selected}
                  onSelect={(selectedContacts = []) => {
                    handleSelect(selectedContacts);
                  }}
                  disabled={selected.length >= secondaryTargetLimit}
                  testId="secondary-target-field"
                  onBlur={(e) => {
                    onBlur({ target: { name } });
                  }}
                />
                {householdMembers.map((member = {}) => {
                  const valueIds = value.map((mem) => mem.id);
                  const isSelected = valueIds.includes(member.id);
                  return (
                    <div key={member.id} style={{ marginTop: '10px' }}>
                      <Checkbox
                        label="Household Member Checkbox"
                        onChange={(checked) => {
                          handleSelect([member], true, checked);
                        }}
                        checked={isSelected}
                        disabled={value?.length >= secondaryTargetLimit}
                      >
                        Add {member?.contact_attributes?.name_full} (Household Member)
                      </Checkbox>
                    </div>
                  );
                })}
              </>
            ) : null}
          </>
        );
      }}
    </CachedContactsResolver>
  );
};

SecondaryTargetField.propTypes = {
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.array,
  contactId: PropTypes.number,
  required: PropTypes.bool,
  field: PropTypes.shape({
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    value: PropTypes.any,
    checked: PropTypes.bool,
    multiple: PropTypes.bool,
  }),
  meta: PropTypes.shape({
    error: PropTypes.string,
    initialError: PropTypes.string,
    initialTouched: PropTypes.bool,
    initialValue: PropTypes.any,
    touched: PropTypes.bool,
    value: PropTypes.any,
  }),
  helpers: PropTypes.shape({
    setValue: PropTypes.func,
    setTouched: PropTypes.func,
    setError: PropTypes.func,
  }),
};

export default SecondaryTargetField;
